import { Box, Grid, IconButton, Tooltip as MuiTooltip, Typography } from "@material-ui/core";
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAppState } from "../../context/zustand";
import { useAxios } from "../../utils/request";
import { EPISODE_MODE_NONE, EPISODE_MODE_SELECTED, getEpisodeSelectionRequest } from "../../utils/episodeSelection";
import { getCollectedWeekRange } from "../../utils/weekInterval";
import { CustomTooltip, getOrderedPlatformKeys, getPlatformDisplayName, getWeekInterval, multiplyObjectsByKey, sumObjectsByKey, sumObjectItems, sumObjectItemsKoefOnly, PLATFORMS } from "../../utils/utils";
import CheckboxList from "../CheckboxList";
import KoefPopover from "../KoefPopover";
import { buildGraphCsvFilename, downloadGraphCsv, getCsvDateRange, getEpisodeCsvColumns, getEpisodeMetadataMap, getWeeklyCsvColumns } from "../../utils/graphCsv";
import { useEpisodeSelectionConsumer } from "../../hooks/useEpisodeSelectionConsumer";





const useStyles = makeStyles(() => ({
    root: {
        paddingInline: 30
    },
}));



export default function PerEpisodeModul({coef,isCumulative}) {
    const classes = useStyles();
    const podcast = useAppState(s => s.podcast?.name);
    const episodeMode = useAppState(s => s.episodeMode);
    const selectedEpisodeIds = useAppState(s => s.selectedEpisodeIds);
    const intervalPreset = useAppState(s => s.intervalPreset);
    const intervalStartWeek = useAppState(s => s.intervalStartWeek);
    const intervalEndWeek = useAppState(s => s.intervalEndWeek);
    const intervalStartDate = useAppState(s => s.intervalStartDate);
    const intervalEndDate = useAppState(s => s.intervalEndDate);
    const episodeMetadata = useAppState(s => s.episodes);
    const setCollectedWeekRange = useAppState(s => s.setCollectedWeekRange);
    const [rawData, setRawData] = useState(false);
    const [episodeCsvRawData, setEpisodeCsvRawData] = useState(false);
    const [episodeCsvRows, setEpisodeCsvRows] = useState([]);
    const [data, setData] = useState([]);
    const [platformKoef, setPlatformKoef] = useState(coef)
    const selectionRequest = getEpisodeSelectionRequest(episodeMode, selectedEpisodeIds);
    const selectedEpisodeParam = selectionRequest.episodes;
    const consumerId = isCumulative ? 'per-episode-cumulative' : 'per-episode-standard';
    const { revision, acknowledgeAfterPaint } = useEpisodeSelectionConsumer(consumerId);

    const updateDataRequest = useAxios(
        ["records", podcast, isCumulative, episodeMode, selectedEpisodeParam, intervalPreset, intervalStartWeek, intervalEndWeek],
        {
            method: 'get',
            url: `/api/records/`,
            params: {
                episodes: selectedEpisodeParam,
                date_from: intervalStartWeek,
                date_to: intervalEndWeek,
                podcast: podcast
            }
        },
        {enabled: Boolean(podcast) && selectionRequest.enabled},
        () => {},
        (err) => {}
    )

    const episodeCsvRequest = useAxios(
        ["records_by_episode_csv", podcast, isCumulative, episodeMode, selectedEpisodeParam, intervalPreset, intervalStartWeek, intervalEndWeek],
        {
            method: 'get',
            url: `/api/allepisodes/`,
            params: {
                episodes: selectedEpisodeParam,
                date_from: intervalStartWeek,
                date_to: intervalEndWeek,
                podcast: podcast
            }
        },
        {enabled: Boolean(podcast) && !isCumulative && selectionRequest.enabled},
        () => {},
        (err) => {}
    )

    useEffect(() => {
        if (episodeMode === EPISODE_MODE_NONE) {
            setRawData(false);
            setData([]);
            return;
        }

        if (updateDataRequest.isFetching) {
            return;
        }

        if (updateDataRequest.error) {
            setRawData(false);
            setData([]);
            return;
        }

        if (updateDataRequest.data?.data) {
            const responseData = updateDataRequest.data.data;
            setRawData(responseData);
            setCollectedWeekRange({
                startWeek: responseData.minDateValue,
                endWeek: responseData.maxDateValue,
            });
            setData(processData(responseData));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [episodeMode, revision, updateDataRequest.data, updateDataRequest.error, updateDataRequest.isFetching]);

    useEffect(() => {
        if (isCumulative || episodeMode === EPISODE_MODE_NONE) {
            setEpisodeCsvRawData(false);
            setEpisodeCsvRows([]);
            return;
        }

        if (episodeCsvRequest.isFetching) {
            return;
        }

        if (episodeCsvRequest.error) {
            setEpisodeCsvRawData(false);
            setEpisodeCsvRows([]);
            return;
        }

        if (episodeCsvRequest.data?.data) {
            const responseData = episodeCsvRequest.data.data;
            setEpisodeCsvRawData(responseData);
            setEpisodeCsvRows(processEpisodeCsvData(responseData.results));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [episodeMode, revision, isCumulative, episodeCsvRequest.data, episodeCsvRequest.error, episodeCsvRequest.isFetching]);

    useEffect(() => {
        if (episodeMode === EPISODE_MODE_NONE) {
            acknowledgeAfterPaint(revision);
            return;
        }

        const graphSettled = !updateDataRequest.isFetching && ['success', 'error'].includes(updateDataRequest.status);
        const csvSettled = isCumulative || (
            !episodeCsvRequest.isFetching && ['success', 'error'].includes(episodeCsvRequest.status)
        );

        if (graphSettled && csvSettled) {
            acknowledgeAfterPaint(revision);
        }
    }, [
        acknowledgeAfterPaint,
        episodeCsvRequest.isFetching,
        episodeCsvRequest.status,
        episodeMode,
        isCumulative,
        revision,
        updateDataRequest.isFetching,
        updateDataRequest.status,
    ]);

    useEffect(() => {
        setPlatformKoef(coef)
    }, [coef])

    useEffect(() => {
        if (episodeMode !== EPISODE_MODE_NONE && rawData) {
            let data = processData(rawData);
            setData(data);
        }
        // eslint-disable-next-line
    }, [episodeMode, platformKoef, isCumulative, intervalPreset, intervalStartWeek, intervalEndWeek])

    useEffect(() => {
        if (episodeMode !== EPISODE_MODE_NONE && episodeCsvRawData?.results) {
            setEpisodeCsvRows(processEpisodeCsvData(episodeCsvRawData.results));
        }
        // eslint-disable-next-line
    }, [episodeMode, episodeCsvRawData, platformKoef, selectedEpisodeParam, episodeMetadata])

    const processData = (resData) => {

        const collectedRange = getCollectedWeekRange(intervalStartWeek, intervalEndWeek, resData.minDateValue, resData.maxDateValue);

        if (!collectedRange) {
            return []
        }

        let data = JSON.parse(JSON.stringify(resData.results));
        let dateInterval = getWeekInterval(collectedRange.startWeek, collectedRange.endWeek);
        let newArray = []


        dateInterval.reduce((acc, curr, index) => {
            if(isCumulative){
                Object.keys(acc).forEach((i) => acc[i] = 0);
            }
            if (curr in resData.results) {
                let newItem = sumObjectsByKey(acc, data[curr]);
                newArray[index] = { 'date': curr, ...newItem };
                return newItem
            }
            else {
                newArray[index] = { 'date': curr, ...acc };
                return acc
            }

        }, {})
        let result = newArray.map(item => (
            { 
                ...multiplyObjectsByKey(item, platformKoef), 
                date: item.date, 
                total: sumObjectItemsKoefOnly(item, platformKoef)
            }))
        return result
    }

    const processEpisodeCsvData = (resData) => {
        if (!resData) {
            return []
        }

        const episodeMetadataMap = getEpisodeMetadataMap(episodeMetadata);
        const totalsByEpisode = Object.values(resData).reduce((acc, weekData) => {
            Object.entries(weekData).forEach(([episode, platforms]) => {
                if (!acc[episode]) {
                    acc[episode] = {};
                }

                Object.entries(platforms).forEach(([platform, quantity]) => {
                    acc[episode][platform] = (acc[episode][platform] || 0) + quantity;
                });
            });

            return acc;
        }, {});

        return Object.entries(totalsByEpisode)
            .filter(([key]) => episodeMode !== EPISODE_MODE_SELECTED || selectedEpisodeIds.map(String).includes(String(key)))
            .map(([key, item]) => ({ id: key, quantity: item }))
            .filter(item => (sumObjectItems(item.quantity, {}) !== 0))
            .map(item => {
                const metadata = episodeMetadataMap[String(item.id)] || {};

                return {
                    id: item.id,
                    name: metadata.name || '',
                    release_date: metadata.release_date || '',
                    ...multiplyObjectsByKey(item.quantity, platformKoef),
                    total: sumObjectItems(item.quantity, platformKoef),
                };
            })
            .sort((a, b) => Number(a.id) - Number(b.id));
    }

    const handleDownloadCsv = () => {
        const isEpisodeLevelCsv = !isCumulative;
        const rows = isEpisodeLevelCsv ? episodeCsvRows : data;
        const columns = isEpisodeLevelCsv ? getEpisodeCsvColumns(platformKoef) : getWeeklyCsvColumns(platformKoef);
        const range = getCsvDateRange({
            intervalPreset,
            intervalStartDate,
            intervalEndDate,
            collectedStartWeek: rawData?.minDateValue,
            collectedEndWeek: rawData?.maxDateValue,
            rows,
            rowDateKey: isEpisodeLevelCsv ? 'release_date' : 'date',
        });

        downloadGraphCsv({
            rows,
            columns,
            filename: buildGraphCsvFilename({
                podcast,
                startDate: range.startDate,
                endDate: range.endDate,
                graph: 'episode',
            }),
        });
    }

    const csvRows = !isCumulative ? episodeCsvRows : data;
    const isCsvDisabled = csvRows.length === 0 || (!isCumulative && episodeCsvRequest.isLoading);
    const isGraphLoading = episodeMode !== EPISODE_MODE_NONE && updateDataRequest.isFetching;

    return (
        <>
            <Grid container spacing={3} className={classes.root}>
                <Grid item lg={12}>
                    <Box paddingY={5} display="flex" justifyContent="space-between" alignItems="center" flexDirection="row" >
                        <Typography align="left" variant='h4'>{`Episode downloads: ${isCumulative ? 'non-' : ''}cumulative`}</Typography>
                        <Box display="flex" flexDirection="row" >
                            <Box paddingRight={2}>
                                <KoefPopover
                                    values={platformKoef}
                                    handleChange={
                                        (value) => {
                                            setPlatformKoef(value)
                                        }
                                    }
                                />
                            </Box>
                            <CheckboxList
                                values={platformKoef}
                                defaultValues={coef}
                                handleChange={
                                    (value) => {
                                        setPlatformKoef(value);
                                    }
                                }
                            />
                            <MuiTooltip title="Download .csv">
                                <span>
                                    <IconButton
                                        aria-label="Download .csv"
                                        color="primary"
                                        disabled={isCsvDisabled}
                                        onClick={handleDownloadCsv}
                                    >
                                        <GetAppIcon />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                        </Box>
                    </Box>

                </Grid>

                <Grid item xs={12} md={12} lg={12}>
                    {episodeMode === EPISODE_MODE_NONE ? (
                        <Box height={500} display="flex" alignItems="center" justifyContent="center">
                            <Typography color="textSecondary">Select at least one episode.</Typography>
                        </Box>
                    ) : isGraphLoading ? (
                        <Box height={500} display="flex" alignItems="center" justifyContent="center">
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <ResponsiveContainer width="100%" height={500}>
                            <AreaChart
                                data={data}
                                margin={{
                                    top: 0,
                                    right: 0,
                                    left: 0,
                                    bottom: 0,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip content={<CustomTooltip key={'perepisode tooltip'}/>} />
                                {data[data.length - 1] && getOrderedPlatformKeys(Object.keys(data[data.length - 1]).filter(i => !['date','total'].includes(i))).map((item, index) => {
                                    return <Area
                                        type="monotone"
                                        key={`Area${index}`}
                                        activeDot={{ strokeWidth: 2 }}
                                        dot={{ strokeWidth: 5 }}
                                        dataKey={item}
                                        name={getPlatformDisplayName(item)}
                                        stackId="1"
                                        stroke={PLATFORMS[item.toUpperCase()].color}
                                        fill={PLATFORMS[item.toUpperCase()].color} />

                                })}
                            </AreaChart>
                        </ResponsiveContainer>
                    )}

                </Grid>

            </Grid >

        </>
    )
}
